import { useState } from "react";
import { motion } from "framer-motion";
import { Check, ArrowRight, Zap, Crown, Building, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useI18n } from "@/lib/i18nContext";

const planDefs = [
  {
    key: "free",
    monthlyPrice: 0,
    annualPrice: 0,
    icon: Zap,
    featureKeys: ["pricing.free.f1", "pricing.free.f2", "pricing.free.f3", "pricing.free.f4"],
    excludedKeys: ["pricing.free.x1", "pricing.free.x2", "pricing.free.x3", "pricing.free.x4"],
    hero: false,
    badgeKey: null as string | null,
    buttonClass: "border-border hover:border-primary/30",
    buttonVariant: "outline" as const,
    ctaSubKey: "pricing.noCreditCard" as string | null,
  },
  {
    key: "pro",
    monthlyPrice: 99,
    annualPrice: 79,
    icon: Crown,
    featureKeys: [
      "pricing.pro.f1", "pricing.pro.f2", "pricing.pro.f3", "pricing.pro.f4",
      "pricing.pro.f5", "pricing.pro.f6", "pricing.pro.f7", "pricing.pro.f8",
      "pricing.pro.f9", "pricing.pro.f10",
    ],
    excludedKeys: [] as string[],
    hero: true,
    badgeKey: "pricing.pro.badge",
    buttonClass: "bg-brand-dark hover:bg-brand-dark/90 text-brand-dark-foreground shadow-md hover:shadow-lg hover:scale-[1.02]",
    buttonVariant: "default" as const,
    ctaSubKey: null as string | null,
  },
  {
    key: "studio",
    monthlyPrice: 179,
    annualPrice: 143,
    icon: Building,
    featureKeys: [
      "pricing.studio.f1", "pricing.studio.f2", "pricing.studio.f3",
      "pricing.studio.f4", "pricing.studio.f5", "pricing.studio.f6",
    ],
    excludedKeys: [] as string[],
    hero: false,
    badgeKey: null as string | null,
    buttonClass: "border-violet-400/50 text-violet-600 dark:text-violet-400 hover:bg-violet-500/10 hover:border-violet-400",
    buttonVariant: "outline" as const,
    ctaSubKey: null as string | null,
  },
  {
    key: "enterprise",
    monthlyPrice: null,
    annualPrice: null,
    icon: Building2,
    featureKeys: [
      "pricing.enterprise.f1", "pricing.enterprise.f2", "pricing.enterprise.f3",
      "pricing.enterprise.f4", "pricing.enterprise.f5",
    ],
    excludedKeys: [] as string[],
    hero: false,
    badgeKey: null as string | null,
    buttonClass: "border-border hover:border-primary/30",
    buttonVariant: "outline" as const,
    ctaSubKey: null as string | null,
  },
];

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, delay, ease: [0.21, 0.47, 0.32, 0.98] as const },
});

export default function LandingPricing({ onGetStarted }: { onGetStarted: () => void }) {
  const [annual, setAnnual] = useState(false);
  const { t } = useI18n();

  return (
    <div>
      <motion.div {...fade()} className="text-center mb-16">
        <h2 className="font-heading text-3xl sm:text-4xl font-medium text-foreground mb-4">
          {t("pricing.title")}
        </h2>
        <p className="text-muted-foreground max-w-md mx-auto">
          {t("pricing.subtitle")}
        </p>
        <div className="flex items-center justify-center gap-3 mt-6">
          <span className={`text-sm font-medium ${!annual ? "text-foreground" : "text-muted-foreground"}`}>{t("pricing.monthly")}</span>
          <Switch checked={annual} onCheckedChange={setAnnual} aria-label={t("pricing.annual")} />
          <span className={`text-sm font-medium ${annual ? "text-foreground" : "text-muted-foreground"}`}>{t("pricing.annual")}</span>
          <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-brand-dark text-brand-dark-foreground">{t("pricing.save20")}</span>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 items-start max-w-6xl mx-auto">
        {planDefs.map((p, i) => (
          <motion.div
            key={p.key}
            {...fade(0.07 * i)}
            className={`group relative bg-card rounded-xl border flex flex-col transition-all duration-300 ${
              p.hero
                ? "border-brand-dark/30 shadow-xl ring-1 ring-brand-dark/15 lg:scale-105 lg:-my-3 z-10 p-7"
                : "border-border hover:border-primary/15 hover:shadow-elevated p-5"
            }`}
          >
            {p.badgeKey && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 text-[11px] font-medium px-3 py-0.5 rounded-full tracking-wide whitespace-nowrap bg-brand-dark text-brand-dark-foreground">
                {t(p.badgeKey)}
              </div>
            )}

            <div
              className={`w-9 h-9 rounded-lg flex items-center justify-center mb-4 transition-colors duration-300 ${
                p.hero
                  ? "bg-brand-dark/10 group-hover:bg-brand-dark/15"
                  : "bg-primary/8 group-hover:bg-primary/12"
              }`}
            >
              <p.icon className={`w-[18px] h-[18px] ${p.hero ? "text-brand-dark" : "text-primary"}`} />
            </div>

            <h3 className="font-heading font-medium text-foreground text-base">{t(`pricing.${p.key}.name`)}</h3>
            <p className="text-xs text-muted-foreground/70 mt-1 leading-snug">{t(`pricing.${p.key}.micro`)}</p>

            <div className="mt-4 mb-5">
              {p.monthlyPrice === null ? (
                <span className="font-heading text-3xl font-medium text-foreground tracking-tight">{t("pricing.custom")}</span>
              ) : (
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="font-heading text-3xl font-medium text-foreground tracking-tight">
                    ${annual && p.monthlyPrice > 0 ? p.annualPrice : p.monthlyPrice}
                  </span>
                  <span className="text-sm text-muted-foreground">{t("pricing.mo")}</span>
                  {annual && p.monthlyPrice > 0 && (
                    <span className="text-sm text-muted-foreground/50 line-through">${p.monthlyPrice}{t("pricing.mo")}</span>
                  )}
                </div>
              )}
            </div>

            <ul className="space-y-2.5 mb-2 flex-1">
              {p.featureKeys.map((fk) => (
                <li key={fk} className="flex items-start gap-2.5 text-sm text-muted-foreground">
                  <Check className={`w-4 h-4 shrink-0 mt-0.5 ${p.hero ? "text-brand-dark" : "text-brand-dark/80"}`} />
                  <span>{t(fk)}</span>
                </li>
              ))}
              {p.excludedKeys.map((fk) => (
                <li key={fk} className="flex items-start gap-2.5 text-sm text-muted-foreground/40 line-through">
                  <span className="w-4 h-4 shrink-0 mt-0.5 text-center">—</span>
                  <span>{t(fk)}</span>
                </li>
              ))}
            </ul>

            <div className="mt-4">
              <Button
                variant={p.buttonVariant}
                className={`w-full gap-2 transition-all duration-200 ${p.buttonClass}`}
                onClick={onGetStarted}
              >
                {t(`pricing.${p.key}.cta`)}
                <ArrowRight className="w-3.5 h-3.5" />
              </Button>
              {p.ctaSubKey && (
                <p className="text-[11px] text-muted-foreground/60 text-center mt-1.5">{t(p.ctaSubKey)}</p>
              )}
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
